import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';

const DMARCFAQ = () => {
  const [openIndices, setOpenIndices] = useState([]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const toggleAccordion = (index) => {
    if (openIndices.includes(index)) {
      setOpenIndices(openIndices.filter((i) => i !== index)); // Close accordion if it's already open
    } else {
      setOpenIndices([...openIndices, index]); // Open the clicked accordion
    }
  };

  const faqItems = [
    {
      question: 'What is DMARC?',
      answer:
        'DMARC (Domain-based Message Authentication, Reporting, and Conformance) is an email authentication protocol that helps protect email domains from unauthorized use, often known as email spoofing. It builds on SPF and DKIM protocols to improve and monitor the protection of the domain from fraudulent email.',
    },
    {
      question: "What happens if I don't have DMARC for my domain?",
      answer:
        "Without DMARC, your domain lacks a layer of protection against email spoofing and phishing. It makes it easier for attackers to send emails that appear to come from your domain, potentially harming your brand's reputation and your recipients' trust.",
    },
    {
      question: 'What are the risks of not implementing DMARC?',
      answer:
        "Not implementing DMARC exposes your domain to being used in phishing scams, which can lead to data breaches, financial loss, and damage to your brand's reputation. It also increases the likelihood of your emails being marked as spam by recipients' email services.",
    },
    {
      question: 'What are the business benefits of implementing DMARC?',
      answer:
        "Implementing DMARC enhances email security, protects your brand's reputation, reduces the risk of email-based attacks, and improves email deliverability. It helps maintain customer trust by ensuring the authenticity of emails sent from your domain.",
    },
    {
      question: 'How does BIMI impact email authentication?',
      answer:
        "BIMI itself doesn't authenticate emails but works in conjunction with DMARC, SPF, and DKIM. It provides a visual indication of authentication by displaying the brand's logo, thus complementing the technical email authentication mechanisms.",
    },
    {
      question: 'How does BIMI enhance email marketing?',
      answer:
        'BIMI (Brand Indicators for Message Identification) allows the display of brand-controlled logos in supported email clients. It helps to authenticate emails, build brand trust, and increase customer engagement by providing a visual identifier for genuine emails.',
    },
  ];

  const handleScrollToPricing = (e) => {
    e.preventDefault();
    const pricingElement = document.getElementById('pricing');
    if (pricingElement) {
      const offsetTop = pricingElement.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div>
      <section className="section">
        <div className="container-default w-container">
          <div className="inner-container _700px---tablet center">
            <div className="inner-container _600px---mbl center">
              <motion.div
                className="mg-bottom-80px"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <div className="inner-container _604px center">
                  <div className="text-center">
                    <div className="mg-bottom-24px">
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        Frequently Asked Questions
                      </div>
                    </div>
                    <h2 className="display-2 color-neutral-100">Have any questions?</h2>
                    <p className="color-neutral-300 mg-bottom-0 paragraph-medium">
                      Discover the essential insights and answers to your DMARC and BIMI queries,
                      ensuring your email practices are both secure and effective for your business
                      needs.
                    </p>
                  </div>
                </div>
              </motion.div>
              <motion.div
                className="w-layout-grid grid-2-columns accordions-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {faqItems.map((item, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className={`accordion-item-wrapper ${openIndices.includes(index) ? 'open' : ''}`}
                    onClick={() => toggleAccordion(index)}
                    style={{
                      backgroundColor: openIndices.includes(index)
                        ? 'rgb(36, 123, 254)'
                        : 'rgb(40, 41, 43)',
                    }}
                  >
                    <div className="accordion-content-wrapper">
                      <div className="accordion-header">
                        <h3 className="text-400 bold color-neutral-100 mg-bottom-0">
                          {item.question}
                        </h3>
                      </div>
                      <div
                        className="acordion-body"
                        style={{
                          display: openIndices.includes(index) ? 'block' : 'none',
                          opacity: openIndices.includes(index) ? 1 : 0,
                          height: openIndices.includes(index) ? 'auto' : 0,
                          transition: 'opacity 0.3s, height 0.3s',
                        }}
                      >
                        <div className="accordion-spacer"></div>
                        <p className="color-neutral-100 mg-bottom-0">{item.answer}</p>
                      </div>
                    </div>
                    <div
                      className="btn-square-secondary small accordion-btn"
                      style={{
                        transform: openIndices.includes(index) ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                        borderColor: openIndices.includes(index)
                          ? 'rgb(255, 255, 255)'
                          : 'rgb(36, 123, 254)',
                      }}
                    >
                      <div
                        className="accordion-item-line-icon horizontal"
                        style={{
                          backgroundColor: openIndices.includes(index)
                            ? 'rgb(255, 255, 255)'
                            : 'rgb(36, 123, 254)',
                          transform: openIndices.includes(index) ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      ></div>
                      <div
                        className="accordion-item-line-icon vertical"
                        style={{
                          backgroundColor: openIndices.includes(index)
                            ? 'rgb(255, 255, 255)'
                            : 'rgb(36, 123, 254)',
                          transform: openIndices.includes(index)
                            ? 'rotate(270deg)'
                            : 'rotate(90deg)',
                        }}
                      ></div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div
                className="text-center mg-top-60px"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                  delay: 0.3,
                }}
                style={{ margin: '48px 0 0' }}
              >
                <Link
                  href="#pricing"
                  className="btn-primary w-button"
                  onClick={handleScrollToPricing}
                >
                  Get Started
                  <span className="line-rounded-icon link-icon-right"></span>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      <div className="divider _0px bg-neutral-700"></div>
    </div>
  );
};

export default DMARCFAQ;
